import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import { parseError } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";
import type { PatientSupplement, PatientSupplementFields } from "@/types/patient-supplement";
import { archivePatientSupplement, createPatientSupplement, listPatientSupplements, listSupplementNames, updatePatientSupplement } from "@/api/provider/user/patient/supplements";
export const patientSupplementQueryKeys = {
  all: ["patient", "supplements"] as const,
  patient: (patientId: string) => [...patientSupplementQueryKeys.all, patientId] as const,
  names: () => [...patientSupplementQueryKeys.all, "names"] as const
};
export function usePatientSupplementsQuery(patientId) {
  const $ = _c(8);
  let t0;
  if ($[0] !== patientId) {
    t0 = patientSupplementQueryKeys.patient(patientId);
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== patientId) {
    t1 = () => listPatientSupplements(patientId);
    $[2] = patientId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!patientId;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
export function useSupplementNamesQuery(enabled) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = patientSupplementQueryKeys.names();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== enabled) {
    t1 = {
      queryKey: t0,
      queryFn: listSupplementNames,
      enabled
    };
    $[1] = enabled;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useQuery(t1);
}
function updateCachedSupplements(patientId: string, update: (current: PatientSupplement[]) => PatientSupplement[]) {
  queryClient.setQueryData<PatientSupplement[] | null>(patientSupplementQueryKeys.patient(patientId), current => update(current ?? []));
}
function rememberSupplementName(name: string) {
  queryClient.setQueryData<string[] | null>(patientSupplementQueryKeys.names(), current => {
    if (!current) return current;
    if (current.some(n => n.toLowerCase() === name.toLowerCase())) {
      return current;
    }
    return [...current, name].sort((a, b) => a.localeCompare(b, undefined, {
      sensitivity: "base"
    }));
  });
}
async function toastSupplementError(title: string, error: Error) {
  toast({
    title,
    description: await parseError(error),
    variant: "error"
  });
}
export function useCreatePatientSupplementMutation(patientId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== patientId) {
    t0 = {
      mutationFn: fields => createPatientSupplement({
        ...fields,
        patientId
      }),
      onSuccess: created => {
        if (!created) {
          return;
        }
        updateCachedSupplements(patientId, current => [created, ...current]);
        rememberSupplementName(created.name);
        toast({
          title: "Supplement added",
          variant: "success"
        });
      },
      onError: _temp
    };
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp(error) {
  return toastSupplementError("Failed to add supplement", error);
}
export function useUpdatePatientSupplementMutation(patientId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== patientId) {
    t0 = {
      mutationFn: _temp2,
      onSuccess: updated => {
        if (!updated) {
          return;
        }
        updateCachedSupplements(patientId, current => current.map(s => s.id === updated.id ? updated : s));
        rememberSupplementName(updated.name);
        toast({
          title: "Supplement updated",
          variant: "success"
        });
      },
      onError: _temp3
    };
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp3(error) {
  return toastSupplementError("Failed to update supplement", error);
}
function _temp2(t0) {
  const {
    id,
    fields
  } = t0;
  return updatePatientSupplement(id, fields);
}
export function useArchivePatientSupplementMutation(patientId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== patientId) {
    t0 = {
      mutationFn: archivePatientSupplement,
      onSuccess: (_, supplementId) => {
        updateCachedSupplements(patientId, current => current.filter(s => s.id !== supplementId));
        toast({
          title: "Supplement removed",
          variant: "success"
        });
      },
      onError: _temp4
    };
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp4(error) {
  return toastSupplementError("Failed to remove supplement", error);
}